import { createLogger } from './utils';

const { log } = createLogger('SyncQueueManager');

/**
 * Manages queueing of messages received during the initial synchronization process.
 */
class SyncQueueManager {
   constructor() {
      this.isSyncInProgress = false;
      this.isProcessingQueue = false;
      this.messageQueue = [];
      this.messageHandler = null;
   }

   /**
    * Sets the handler function to be used for processing queued messages.
    * @param {Function} handler - The message handler function.
    */
   init(handler) {
      this.messageHandler = handler;
   }

   /**
    * Starts the synchronization process, enabling message queueing.
    */
   startSync() {
      log('Sync', '🚀 Starting sync - message queueing enabled');
      this.isSyncInProgress = true;
   }

   /**
    * Ends the synchronization process and triggers queue processing.
    */
   async endSync() {
      if (!this.isSyncInProgress) return;

      log('Sync', '🏁 Ending sync - starting queue processing');
      this.isSyncInProgress = false;
      await this.processQueue();
   }

   /**
    * Checks if incoming data should be queued.
    * @returns {boolean} True if sync is in progress or queue is currently being processed.
    */
   shouldQueue() {
      return this.isSyncInProgress || this.isProcessingQueue;
   }

   /**
    * Adds a message to the queue.
    * @param {Object} data - The message data to queue.
    */
   enqueue(data) {
      log('Queue', `📥 Enqueueing message type: ${data?.msgType}`);
      this.messageQueue.push(data);
   }

   /**
    * Processes all queued messages sequentially.
    */
   async processQueue() {
      if (this.isProcessingQueue || this.messageQueue.length === 0) {
         if (this.messageQueue.length === 0) {
            log('Queue', '✅ Queue is empty');
         }
         return;
      }

      this.isProcessingQueue = true;
      log('Queue', `🔄 Processing ${this.messageQueue.length} queued messages`);

      try {
         while (this.messageQueue.length > 0) {
            const data = this.messageQueue.shift();
            if (this.messageHandler) {
               try {
                  await this.messageHandler(data);
               } catch (err) {
                  log('Error', `❌ Error processing queued message: ${err.message}`);
               }
            }
         }
      } finally {
         this.isProcessingQueue = false;
         log('Queue', '✅ Queue processing complete');

         // If more messages were added while processing (race condition coverage)
         if (this.messageQueue.length > 0) {
            log('Queue', '♻️ Processing additional messages received during processing');
            await this.processQueue();
         }
      }
   }
}

export const syncQueueManager = new SyncQueueManager();
